import React, { useEffect, useMemo, useCallback } from 'react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { colors, Grid, Card, CardContent } from '@mui/material';
import { Page, Map } from 'components';
import L from 'leaflet';
import _ from 'underscore';
import { enqueueSnackbar } from 'actions';
import { onUpdateLegend } from 'components/Map/actions/MapActions';
import {
	SetFormState, ResetFields, GetDriveTestsCities, GetDriveTestsDrives, GetFailuresFilters,
	SetSelectedDates, SetSelectedLogs,
	GetFailuresFiltersCompare, SetSelectedDatesCompare, GetSitesIds, GetCustomLayer,
	ToggleCarriersColorsDialog, ToggleCarriersColorsPickerDialog, ToggleCustomLayerDialog,
	ToggleXcapToolDialog } from '../actions/DualMapActions';
import CompareBox from './components/CompareBox';
import FiltersTop from './components/FiltersTop';
import FiltersTopCompare from './components/FiltersTopCompare';
import FiltersSide from './components/FiltersSide';
import FiltersSideCompare from './components/FiltersSideCompare';
import AdditionalInfo from './components/AdditionalInfo';
import CarriersColorsDialog from './components/Components/CarriersColorsDialog';
import CarriersColorsPickerDialog from './components/Components/CarriersColorsPickerDialog';
import CustomLayerDialog from './components/Components/CustomLayerDialog';
import XcapToolFilesUploadDialog from './components/XcapToolFilesUploadDialog';

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
	},
	container: {
		marginTop: theme.spacing(3),
	},
	tabs: {
		marginTop: theme.spacing(3),
	},
	content: {
		marginTop: theme.spacing(3),
	},
	generate: {
		color: theme.palette.white,
		backgroundColor: colors.green[600],
		'&:hover': {
			backgroundColor: colors.green[900],
		},
	},
}));

const DualMap = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.dualMapReducer.formState);
	const maps = useSelector(state => state.mapReducer.maps);

	// Commun
	const customLayer = useSelector((state) => state.dualMapReducer.customLayer);
	const sitesIds = useSelector(state => state.dualMapReducer.sitesIds);

	// Map
	const selectedDates = useSelector(state => state.dualMapReducer.selectedDates);
	const mapOne = maps.filter(item => item.id === 'dual-map')[0];
	const driveTest = useSelector(state => state.dualMapReducer.driveTest);
	const ueRoute = useSelector(state => state.dualMapReducer.ueRoute);
	const driveTestScanner = useSelector(state => state.dualMapReducer.driveTestScanner);
	const scannerRoute = useSelector(state => state.dualMapReducer.scannerRoute);
	const crowdsourcing = useSelector(state => state.dualMapReducer.crowdsourcing);

	// Map Compare
	const selectedDatesCompare = useSelector(state => state.dualMapReducer.selectedDatesCompare);
	const mapTwo = maps.filter(item => item.id === 'dual-map-compare')[0];
	const driveTestCompare = useSelector(state => state.dualMapReducer.driveTestCompare);
	const driveTestScannerCompare = useSelector(state => state.dualMapReducer.driveTestScannerCompare);
	const scannerRouteCompare = useSelector(state => state.dualMapReducer.scannerRouteCompare);
	const crowdsourcingCompare = useSelector(state => state.dualMapReducer.crowdsourcingCompare);

	const userId = useSelector(state => state.session.user.id);
	const role = useSelector(state => state.session.user.role);
	const userOperator = useSelector(state => state.session.user.operator);

	// Other
	const carriersColorsDialogVisibility = useSelector((state) => state.dualMapReducer.carriersColorsDialogVisibility);
	const carriersColorsPickerDialogVisibility = useSelector((state) => state.dualMapReducer.carriersColorsPickerDialogVisibility);
	const customLayerDialogVisibility = useSelector((state) => state.dualMapReducer.customLayerDialogVisibility);
	const currentColorCode = useSelector((state) => state.dualMapReducer.currentColorCode);
	const xcapToolDialogVisibility = useSelector((state) => state.dualMapReducer.xcapToolDialogVisibility);

	const _removeLayerById = (map, id) => {
		map.RemoveLayerById(id);
	};

	const _getData = useCallback(() => {
		dispatch(GetSitesIds());
		// dispatch(ResetFields());
		dispatch(GetDriveTestsCities(userId, userOperator));
		dispatch(GetDriveTestsDrives());
		// Map
		dispatch(GetFailuresFilters(role));
		dispatch(SetSelectedDates([]));
		dispatch(SetSelectedLogs([]));
		dispatch(GetCustomLayer(userId));
		// Map Compare
		dispatch(GetFailuresFiltersCompare(role));
		dispatch(SetSelectedDatesCompare([]));
		// if (formState.values.mapCompare === undefined) {
		// 	dispatch(ResetFields());
		// }
	}, []);

	useEffect(() => {
		_getData();
	}, [_getData]);

	useMemo(() => {
		if (mapOne) {
			_removeLayerById(mapOne.control, 'voice');
		}
	}, [sitesIds]);

	const makeDetailsTable = (item) => {
		const table = `<table class="table table-striped">
							<tr>
							<th>Failure Id: </th>
							<td>${item.failureId}</td>
							</tr>
							<tr>
							<th>Failure Type: </th>
							<td>${item.failureType}</td>
							</tr>
							<tr>
							<th>CFN 1 Level: </th>
							<td>${item.cfn1Level}</td>
							</tr>
							<tr>
							<th>CFN 2 Level: </th>
							<td>${item.cfn2Level}</td>
							</tr>
							<tr>
							<th>CFN 3 Level: </th>
							<td>${item.cfn3Level}</td>
							</tr>
							<tr>
							<th>Latitude: </th>
							<td>${item.latitude}</td>
							</tr>
							<tr>
							<th>Longitude: </th>
							<td>${item.longitude}</td>
							</tr>
						</table>`;
		return table;
	};

	const makeDetailsTableKpis = (item) => {
		const table = `<table class="table table-striped">

						<tr>
						<th>Level: </th>
						<td>${item.kpi}</td>
						</tr>

						<tr>
						<th>Latitude: </th>
						<td>${item.latitude}</td>
						</tr>

						<tr>
						<th>Longitude: </th>
						<td>${item.longitude}</td>
						</tr>

					</table>`;

		return table;
	};

	const makeDetailsTableKpisUeRoute = (item) => {
		const table = `<table class="table table-striped">

						<tr>
						<th>Latitude: </th>
						<td>${item.latitude}</td>
						</tr>

						<tr>
						<th>Longitude: </th>
						<td>${item.longitude}</td>
						</tr>

					</table>`;

		return table;
	};

	const makeDetailsTableKpisScannerRoute = (item) => {
		const table = `<table class="table table-striped">

						<tr>
						<th>Latitude: </th>
						<td>${item.latitude}</td>
						</tr>

						<tr>
						<th>Longitude: </th>
						<td>${item.longitude}</td>
						</tr>

					</table>`;

		return table;
	};

	const _addLayerKpi = (map, data, legend, fitBounds, layerName) => {
		map.RemoveLayerById(layerName);
		const markers = [];

		if (!_.isEmpty(data)) {
			data.forEach(item => {
				const marker = L.circle([item.latitude, item.longitude], {
					color: item.color,
					fillColor: item.color,
					fillOpacity: 0.8,
					radius: 10
				});
				markers.push(marker.bindPopup(`<br>${makeDetailsTableKpis(item)}<br>`, { autoPan: false }));
			});

			const layer = L.layerGroup(markers, { id: layerName });
			layer.addTo(map.mapContext.map);
			const group = new L.featureGroup(markers);
			if (fitBounds) {
				map.mapContext.map.fitBounds(group.getBounds());
			}
			dispatch(onUpdateLegend({ id: map.mapContext.id, newLegend: legend }));
		}
	};

	const _addLayerKpiUeRoute = (map, data, legend, fitBounds, layerName) => {
		map.RemoveLayerById(layerName);
		const markers = [];

		if (!_.isEmpty(data)) {
			data.forEach(item => {
				const marker = L.circle([item.latitude, item.longitude], {
					color: item.color,
					fillColor: item.color,
					fillOpacity: 0.8,
					radius: 10
				});
				markers.push(marker.bindPopup(`<br>${makeDetailsTableKpisUeRoute(item)}<br>`, { autoPan: false }));
			});

			const layer = L.layerGroup(markers, { id: layerName });
			layer.addTo(map.mapContext.map);
			const group = new L.featureGroup(markers);
			if (fitBounds) {
				map.mapContext.map.fitBounds(group.getBounds());
			}
			dispatch(onUpdateLegend({ id: map.mapContext.id, newLegend: legend }));
		}
	};

	const _addLayerKpiScanner = (map, data, legend, fitBounds, layerName) => {
		map.RemoveLayerById(layerName);
		const markers = [];

		if (!_.isEmpty(data)) {
			data.forEach(item => {
				const marker = L.circle([item.latitude, item.longitude], {
					color: item.color,
					fillColor: item.color,
					fillOpacity: 0.8,
					radius: 10
				});
				markers.push(marker.bindPopup(`<br>${makeDetailsTableKpis(item)}<br>`, { autoPan: false }));
			});

			const layer = L.layerGroup(markers, { id: layerName });
			layer.addTo(map.mapContext.map);
			const group = new L.featureGroup(markers);
			if (fitBounds) {
				map.mapContext.map.fitBounds(group.getBounds());
			}
			dispatch(onUpdateLegend({ id: map.mapContext.id, newLegend: legend }));
		}
	};

	const _addLayerKpiScannerRoute = (map, data, legend, fitBounds, layerName) => {
		map.RemoveLayerById(layerName);
		const markers = [];

		if (!_.isEmpty(data)) {
			data.forEach(item => {
				const marker = L.circle([item.latitude, item.longitude], {
					color: item.color,
					fillColor: item.color,
					fillOpacity: 0.8,
					radius: 10
				});
				markers.push(marker.bindPopup(`<br>${makeDetailsTableKpisScannerRoute(item)}<br>`, { autoPan: false }));
			});

			const layer = L.layerGroup(markers, { id: layerName });
			layer.addTo(map.mapContext.map);
			const group = new L.featureGroup(markers);
			if (fitBounds) {
				map.mapContext.map.fitBounds(group.getBounds());
			}
			dispatch(onUpdateLegend({ id: map.mapContext.id, newLegend: legend }));
		}
	};

	const _addCustomLayer = (map) => {
		map.RemoveLayerById('CustomLayer');
		const geojsonLayer = L.geoJSON(customLayer, {
			// layer id
			id: 'CustomLayer',
			// draw layer
			pointToLayer(feature, latlng) {
				// Check if the geometry type is "Point"
				if (feature.geometry.type === 'Point') {
					return L.circleMarker(latlng, {
						radius: 5 // Adjust the radius as needed
					});
				}
				// if is not "Point"
				return L.marker(latlng);
			},
			// draw properties
			onEachFeature(feature, layer) {
				let labelContent = '';

				if (feature.properties) {
					const propertyKeys = Object.keys(feature.properties);
					propertyKeys.forEach((prop) => {
						labelContent += `${prop}: ${feature.properties[prop]}<br>`;
					});
				}

				if (labelContent !== '') {
					layer.bindTooltip(labelContent, { sticky: true });
				}
			}
		});
		// Add the GeoJSON layer to the map when needed
		geojsonLayer.addTo(map.mapContext.map);
	};

	// Map
	useMemo(() => {
		if (mapOne) {
			_removeLayerById(mapOne.control, 'topologyColors');
			_removeLayerById(mapOne.control, 'topologyColorsLabels');
			if (driveTest.driveTestLegend !== undefined) {
				if (driveTest.driveTestLegend.title !== 'none') {
					if (selectedDates.length > 0) {
						if (driveTest.driveTestLegend.title === 'empty') {
							dispatch(enqueueSnackbar({ message: 'No data found for UE', options: { variant: 'warning' } }));
							_addLayerKpi(
								mapOne.control, driveTest.driveTestPoints,
								driveTest.driveTestLegend || '', false,
								'kpis'
							);
						} else if (formState.values.selectedKpiTech === '5g') {
							_addLayerKpi(
								mapOne.control, driveTest.driveTestPoints,
								driveTest.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
							if (driveTest.driveTestLegend !== undefined) {
								if (driveTest.driveTestLegend.title === 'PCI') {
									const _map = mapOne.instance;
									const mapBounds = _map.getBounds();
									const mapBoundsPoints = {
										northEast: mapBounds._northEast,
										southWest: mapBounds._southWest
									};
									if (driveTest.topologyColors.length !== 0) {
										mapOne.control.updateTopologyCarriersColors(driveTest.topologyColors, 'topologyColors');
										mapOne.control.addTopologyLabels(driveTest.topologyColors, 'topologyColorsLabels');
									}
								}
							}
						} else if (formState.values.selectedKpiTech === '4g') {
							_addLayerKpi(
								mapOne.control, driveTest.driveTestPoints,
								driveTest.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
							if (driveTest.driveTestLegend !== undefined) {
								if (driveTest.driveTestLegend.title === 'PCI') {
									const _map = mapOne.instance;
									const mapBounds = _map.getBounds();
									const mapBoundsPoints = {
										northEast: mapBounds._northEast,
										southWest: mapBounds._southWest
									};
									if (driveTest.topologyColors.length !== 0) {
										mapOne.control.updateTopologyCarriersColors(driveTest.topologyColors, 'topologyColors');
										mapOne.control.addTopologyLabels(driveTest.topologyColors, 'topologyColorsLabels');
									}
								}
							}
						} else if (formState.values.selectedKpiTech === '3g') {
							_addLayerKpi(
								mapOne.control, driveTest.driveTestPoints,
								driveTest.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
							if (driveTest.driveTestLegend !== undefined) {
								if (driveTest.driveTestLegend.title === 'SC') {
									const _map = mapOne.instance;
									const mapBounds = _map.getBounds();
									const mapBoundsPoints = {
										northEast: mapBounds._northEast,
										southWest: mapBounds._southWest
									};
									if (driveTest.topologyColors.length !== 0) {
										mapOne.control.updateTopologyCarriersColors(driveTest.topologyColors, 'topologyColors');
										mapOne.control.addTopologyLabels(driveTest.topologyColors, 'topologyColorsLabels');
									}
								}
							}
						} else if (formState.values.selectedKpiTech === '2g') {
							_addLayerKpi(
								mapOne.control, driveTest.driveTestPoints,
								driveTest.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
							if (driveTest.driveTestLegend !== undefined) {
								if (driveTest.driveTestLegend.title === 'BSIC') {
									const _map = mapOne.instance;
									const mapBounds = _map.getBounds();
									const mapBoundsPoints = {
										northEast: mapBounds._northEast,
										southWest: mapBounds._southWest
									};
									if (driveTest.topologyColors.length !== 0) {
										mapOne.control.updateTopologyCarriersColors(driveTest.topologyColors, 'topologyColors');
										mapOne.control.addTopologyLabels(driveTest.topologyColors, 'topologyColorsLabels');
									}
								}
							}
						} else if (formState.values.selectedKpiTech === 'common') {
							_addLayerKpi(
								mapOne.control, driveTest.driveTestPoints,
								driveTest.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
						}
					}
				} else {
					_addLayerKpi(
						mapOne.control, driveTest.driveTestPoints,
						driveTest.driveTestLegend || '', false,
						'kpis'
					);
				}
			}
		}
	}, [driveTest]);

	useMemo(() => {
		if (mapOne && ueRoute) {
			_addLayerKpiUeRoute(
				mapOne.control, ueRoute.driveTestPoints,
				ueRoute.driveTestLegend || '', formState.values.mapAutoZoom,
				'ueRoute'
			);
		}
	}, [ueRoute]);

	useMemo(() => {
		if (mapOne) {
			_removeLayerById(mapOne.control, 'topologyColors');
			_removeLayerById(mapOne.control, 'topologyColorsLabels');
			if (driveTestScanner.driveTestLegend !== undefined) {
				if (driveTestScanner.driveTestLegend.title !== 'none') {
					if (selectedDates.length > 0) {
						if (driveTestScanner.driveTestLegend.title === 'empty') {
							dispatch(enqueueSnackbar({ message: 'No data found for Scanner', options: { variant: 'warning' } }));
							_addLayerKpiScanner(
								mapOne.control, driveTestScanner.driveTestPoints,
								driveTestScanner.driveTestLegend || '', false,
								'kpis'
							);
						} else if (formState.values.selectedKpiScannerTech === '5g') {
							_addLayerKpiScanner(
								mapOne.control, driveTestScanner.driveTestPoints,
								driveTestScanner.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
							if (driveTestScanner.driveTestLegend !== undefined) {
								if (driveTestScanner.driveTestLegend.title === 'PCI') {
									const _map = mapOne.control.mapContext.map;
									const mapBounds = _map.getBounds();
									const mapBoundsPoints = {
										northEast: mapBounds._northEast,
										southWest: mapBounds._southWest
									};
									if (driveTestScanner.topologyColors.length !== 0) {
										mapOne.control.updateTopologyCarriersColors(driveTestScanner.topologyColors, 'topologyColors');
										mapOne.control.addTopologyLabels(driveTestScanner.topologyColors, 'topologyColorsLabels');
									}
								}
							}
						} else if (formState.values.selectedKpiScannerTech === '4g') {
							_addLayerKpiScanner(
								mapOne.control, driveTestScanner.driveTestPoints,
								driveTestScanner.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
							if (driveTestScanner.driveTestLegend !== undefined) {
								if (driveTestScanner.driveTestLegend.title === 'PCI') {
									const _map = mapOne.control.mapContext.map;
									const mapBounds = _map.getBounds();
									const mapBoundsPoints = {
										northEast: mapBounds._northEast,
										southWest: mapBounds._southWest
									};
									if (driveTestScanner.topologyColors.length !== 0) {
										mapOne.control.updateTopologyCarriersColors(driveTestScanner.topologyColors, 'topologyColors');
										mapOne.control.addTopologyLabels(driveTestScanner.topologyColors, 'topologyColorsLabels');
									}
								}
							}
						} else if (formState.values.selectedKpiScannerTech === '3g') {
							_addLayerKpiScanner(
								mapOne.control, driveTestScanner.driveTestPoints,
								driveTestScanner.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
							if (driveTestScanner.driveTestLegend !== undefined) {
								if (driveTestScanner.driveTestLegend.title === 'SC') {
									const _map = mapOne.control.mapContext.map;
									const mapBounds = _map.getBounds();
									const mapBoundsPoints = {
										northEast: mapBounds._northEast,
										southWest: mapBounds._southWest
									};
									if (driveTestScanner.topologyColors.length !== 0) {
										mapOne.control.updateTopologyCarriersColors(driveTestScanner.topologyColors, 'topologyColors');
										mapOne.control.addTopologyLabels(driveTestScanner.topologyColors, 'topologyColorsLabels');
									}
								}
							}
						} else if (formState.values.selectedKpiScannerTech === '2g') {
							_addLayerKpiScanner(
								mapOne.control, driveTestScanner.driveTestPoints,
								driveTestScanner.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
							if (driveTestScanner.driveTestLegend !== undefined) {
								if (driveTestScanner.driveTestLegend.title === 'BSIC') {
									const _map = mapOne.control.mapContext.map;
									const mapBounds = _map.getBounds();
									const mapBoundsPoints = {
										northEast: mapBounds._northEast,
										southWest: mapBounds._southWest
									};
									if (driveTestScanner.topologyColors.length !== 0) {
										mapOne.control.updateTopologyCarriersColors(driveTestScanner.topologyColors, 'topologyColors');
										mapOne.control.addTopologyLabels(driveTestScanner.topologyColors, 'topologyColorsLabels');
									}
								}
							}
						}
					}
				} else {
					_addLayerKpiScanner(
						mapOne.control, driveTestScanner.driveTestPoints,
						driveTestScanner.driveTestLegend || '', false,
						'kpis'
					);
				}
			}
		}
	}, [driveTestScanner]);

	useMemo(() => {
		if (mapOne && scannerRoute) {
			_addLayerKpiScannerRoute(
				mapOne.control, scannerRoute.driveTestPoints,
				scannerRoute.driveTestLegend || '', formState.values.mapAutoZoom,
				'scannerRoute'
			);
		}
	}, [scannerRoute]);

	// Crowdsourcing
	useMemo(() => {
		if (mapOne) {
			_removeLayerById(mapOne.control, 'topologyColors');
			_removeLayerById(mapOne.control, 'topologyColorsLabels');
			if (crowdsourcing.driveTestLegend !== undefined) {
				if (crowdsourcing.driveTestLegend.title !== 'none') {
					if (selectedDates.length > 0) {
						if (crowdsourcing.driveTestLegend.title === 'empty') {
							dispatch(enqueueSnackbar({ message: 'No data found for UE', options: { variant: 'warning' } }));
							_addLayerKpi(
								mapOne.control, crowdsourcing.driveTestPoints,
								crowdsourcing.driveTestLegend || '', false,
								'kpis'
							);
						} else if (formState.values.selectedKpiCrowdsourcingTech === '5g') {
							_addLayerKpi(
								mapOne.control, crowdsourcing.driveTestPoints,
								crowdsourcing.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
							if (crowdsourcing.driveTestLegend !== undefined) {
								if (crowdsourcing.driveTestLegend.title === 'PCI') {
									const _map = mapOne.instance;
									const mapBounds = _map.getBounds();
									const mapBoundsPoints = {
										northEast: mapBounds._northEast,
										southWest: mapBounds._southWest
									};
									if (crowdsourcing.topologyColors.length !== 0) {
										mapOne.control.updateTopologyCarriersColors(crowdsourcing.topologyColors, 'topologyColors');
										mapOne.control.addTopologyLabels(crowdsourcing.topologyColors, 'topologyColorsLabels');
									}
								}
							}
						} else if (formState.values.selectedKpiCrowdsourcingTech === '4g') {
							_addLayerKpi(
								mapOne.control, crowdsourcing.driveTestPoints,
								crowdsourcing.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
							if (crowdsourcing.driveTestLegend !== undefined) {
								if (crowdsourcing.driveTestLegend.title === 'PCI') {
									const _map = mapOne.instance;
									const mapBounds = _map.getBounds();
									const mapBoundsPoints = {
										northEast: mapBounds._northEast,
										southWest: mapBounds._southWest
									};
									if (crowdsourcing.topologyColors.length !== 0) {
										mapOne.control.updateTopologyCarriersColors(crowdsourcing.topologyColors, 'topologyColors');
										mapOne.control.addTopologyLabels(crowdsourcing.topologyColors, 'topologyColorsLabels');
									}
								}
							}
						} else if (formState.values.selectedKpiCrowdsourcingTech === '3g') {
							_addLayerKpi(
								mapOne.control, crowdsourcing.driveTestPoints,
								crowdsourcing.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
							if (crowdsourcing.driveTestLegend !== undefined) {
								if (crowdsourcing.driveTestLegend.title === 'SC') {
									const _map = mapOne.instance;
									const mapBounds = _map.getBounds();
									const mapBoundsPoints = {
										northEast: mapBounds._northEast,
										southWest: mapBounds._southWest
									};
									if (crowdsourcing.topologyColors.length !== 0) {
										mapOne.control.updateTopologyCarriersColors(crowdsourcing.topologyColors, 'topologyColors');
										mapOne.control.addTopologyLabels(crowdsourcing.topologyColors, 'topologyColorsLabels');
									}
								}
							}
						} else if (formState.values.selectedKpiCrowdsourcingTech === '2g') {
							_addLayerKpi(
								mapOne.control, crowdsourcing.driveTestPoints,
								crowdsourcing.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
							if (driveTest.driveTestLegend !== undefined) {
								if (crowdsourcing.driveTestLegend.title === 'BSIC') {
									const _map = mapOne.instance;
									const mapBounds = _map.getBounds();
									const mapBoundsPoints = {
										northEast: mapBounds._northEast,
										southWest: mapBounds._southWest
									};
									if (crowdsourcing.topologyColors.length !== 0) {
										mapOne.control.updateTopologyCarriersColors(crowdsourcing.topologyColors, 'topologyColors');
										mapOne.control.addTopologyLabels(crowdsourcing.topologyColors, 'topologyColorsLabels');
									}
								}
							}
						}
					}
				} else {
					_addLayerKpi(
						mapOne.control, crowdsourcing.driveTestPoints,
						crowdsourcing.driveTestLegend || '', false,
						'kpis'
					);
				}
			}
		}
	}, [crowdsourcing]);

	// Map Compare
	useMemo(() => {
		if (mapTwo) {
			_removeLayerById(mapOne.control, 'topologyColors');
			_removeLayerById(mapOne.control, 'topologyColorsLabels');
			if (driveTestCompare.driveTestLegend !== undefined) {
				if (driveTestCompare.driveTestLegend.title !== 'none') {
					if (selectedDatesCompare.length > 0) {
						if (driveTestCompare.driveTestLegend.title === 'empty') {
							dispatch(enqueueSnackbar({ message: 'No data found for UE', options: { variant: 'warning' } }));
							_addLayerKpi(
								mapTwo.control, driveTestCompare.driveTestPoints,
								driveTestCompare.driveTestLegend || '', false,
								'kpis'
							);
						} else if (formState.values.selectedKpiTechCompare === '5g') {
							_addLayerKpi(
								mapTwo.control, driveTestCompare.driveTestPoints,
								driveTestCompare.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
							if (driveTestCompare.driveTestLegend !== undefined) {
								if (driveTestCompare.driveTestLegend.title === 'PCI') {
									const _map = mapTwo.instance;
									const mapBounds = _map.getBounds();
									const mapBoundsPoints = {
										northEast: mapBounds._northEast,
										southWest: mapBounds._southWest
									};
									if (driveTestCompare.topologyColors.length !== 0) {
										mapTwo.control.updateTopologyCarriersColors(driveTestCompare.topologyColors, 'topologyColors');
										mapTwo.control.addTopologyLabels(driveTestCompare.topologyColors, 'topologyColorsLabels');
									}
								}
							}
						} else if (formState.values.selectedKpiTechCompare === '4g') {
							_addLayerKpi(
								mapTwo.control, driveTestCompare.driveTestPoints,
								driveTestCompare.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
							if (driveTestCompare.driveTestLegend !== undefined) {
								if (driveTestCompare.driveTestLegend.title === 'PCI') {
									const _map = mapTwo.instance;
									const mapBounds = _map.getBounds();
									const mapBoundsPoints = {
										northEast: mapBounds._northEast,
										southWest: mapBounds._southWest
									};
									if (driveTestCompare.topologyColors.length !== 0) {
										mapTwo.control.updateTopologyCarriersColors(driveTestCompare.topologyColors, 'topologyColors');
										mapTwo.control.addTopologyLabels(driveTestCompare.topologyColors, 'topologyColorsLabels');
									}
								}
							}
						} else if (formState.values.selectedKpiTechCompare === '3g') {
							_addLayerKpi(
								mapTwo.control, driveTestCompare.driveTestPoints,
								driveTestCompare.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
							if (driveTestCompare.driveTestLegend !== undefined) {
								if (driveTestCompare.driveTestLegend.title === 'SC') {
									const _map = mapTwo.instance;
									const mapBounds = _map.getBounds();
									const mapBoundsPoints = {
										northEast: mapBounds._northEast,
										southWest: mapBounds._southWest
									};
									if (driveTestCompare.topologyColors.length !== 0) {
										mapTwo.control.updateTopologyCarriersColors(driveTestCompare.topologyColors, 'topologyColors');
										mapTwo.control.addTopologyLabels(driveTestCompare.topologyColors, 'topologyColorsLabels');
									}
								}
							}
						} else if (formState.values.selectedKpiTechCompare === '2g') {
							_addLayerKpi(
								mapTwo.control, driveTestCompare.driveTestPoints,
								driveTestCompare.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
							if (driveTestCompare.driveTestLegend !== undefined) {
								if (driveTestCompare.driveTestLegend.title === 'BSIC') {
									const _map = mapTwo.instance;
									const mapBounds = _map.getBounds();
									const mapBoundsPoints = {
										northEast: mapBounds._northEast,
										southWest: mapBounds._southWest
									};
									if (driveTestCompare.topologyColors.length !== 0) {
										mapTwo.control.updateTopologyCarriersColors(driveTestCompare.topologyColors, 'topologyColors');
										mapTwo.control.addTopologyLabels(driveTestCompare.topologyColors, 'topologyColorsLabels');
									}
								}
							}
						} else if (formState.values.selectedKpiTechCompare === 'common') {
							_addLayerKpi(
								mapTwo.control, driveTestCompare.driveTestPoints,
								driveTestCompare.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
						}
					}
				} else {
					_addLayerKpi(
						mapTwo.control, driveTestCompare.driveTestPoints,
						driveTestCompare.driveTestLegend || '', false,
						'kpis'
					);
				}
			}
		}
	}, [driveTestCompare]);

	useMemo(() => {
		if (mapTwo) {
			_removeLayerById(mapTwo.control, 'topologyColors');
			if (driveTestScannerCompare.driveTestLegend !== undefined) {
				if (driveTestScannerCompare.driveTestLegend.title !== 'none') {
					if (selectedDatesCompare.length > 0) {
						if (driveTestScannerCompare.driveTestLegend.title === 'empty') {
							dispatch(enqueueSnackbar({ message: 'No data found for Scanner', options: { variant: 'warning' } }));
							_addLayerKpiScanner(
								mapTwo.control, driveTestScannerCompare.driveTestPoints,
								driveTestScannerCompare.driveTestLegend || '', false,
								'kpis'
							);
						} else if (formState.values.selectedKpiScannerTechCompare === '5g') {
							_addLayerKpiScanner(
								mapTwo.control, driveTestScannerCompare.driveTestPoints,
								driveTestScannerCompare.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
							if (driveTestScannerCompare.driveTestLegend.title === 'PCI') {
								const _map = mapTwo.control.mapContext.map;
								const mapBounds = _map.getBounds();
								const mapBoundsPoints = {
									northEast: mapBounds._northEast,
									southWest: mapBounds._southWest
								};
								mapTwo.control.updateTopologyCarriersColors(driveTestScannerCompare.topologyColors, 'topologyColors');
								mapTwo.control.addTopologyLabels(driveTestCompare.topologyColors, 'topologyColorsLabels');
							}
						} else if (formState.values.selectedKpiScannerTechCompare === '4g') {
							_addLayerKpiScanner(
								mapTwo.control, driveTestScannerCompare.driveTestPoints,
								driveTestScannerCompare.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
							if (driveTestScannerCompare.driveTestLegend.title === 'PCI') {
								const _map = mapTwo.control.mapContext.map;
								const mapBounds = _map.getBounds();
								const mapBoundsPoints = {
									northEast: mapBounds._northEast,
									southWest: mapBounds._southWest
								};
								mapTwo.control.updateTopologyCarriersColors(driveTestScannerCompare.topologyColors, 'topologyColors');
								mapTwo.control.addTopologyLabels(driveTestCompare.topologyColors, 'topologyColorsLabels');
							}
						} else if (formState.values.selectedKpiScannerTechCompare === '3g') {
							_addLayerKpiScanner(
								mapTwo.control, driveTestScannerCompare.driveTestPoints,
								driveTestScannerCompare.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
							if (driveTestScannerCompare.driveTestLegend.title === 'PCI') {
								const _map = mapTwo.control.mapContext.map;
								const mapBounds = _map.getBounds();
								const mapBoundsPoints = {
									northEast: mapBounds._northEast,
									southWest: mapBounds._southWest
								};
								mapTwo.control.updateTopologyCarriersColors(driveTestScannerCompare.topologyColors, 'topologyColors');
								mapTwo.control.addTopologyLabels(driveTestCompare.topologyColors, 'topologyColorsLabels');
							}
						} else if (formState.values.selectedKpiScannerTechCompare === '2g') {
							_addLayerKpiScanner(
								mapTwo.control, driveTestScannerCompare.driveTestPoints,
								driveTestScannerCompare.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
							if (driveTestScannerCompare.driveTestLegend.title === 'BSIC') {
								const _map = mapTwo.control.mapContext.map;
								const mapBounds = _map.getBounds();
								const mapBoundsPoints = {
									northEast: mapBounds._northEast,
									southWest: mapBounds._southWest
								};
								mapTwo.control.updateTopologyCarriersColors(driveTestScannerCompare.topologyColors, 'topologyColors');
								mapTwo.control.addTopologyLabels(driveTestCompare.topologyColors, 'topologyColorsLabels');
							}
						}
					}
				} else {
					_addLayerKpiScanner(
						mapTwo.control, driveTestScannerCompare.driveTestPoints,
						driveTestScannerCompare.driveTestLegend || '', false,
						'kpis'
					);
				}
			}
		}
	}, [driveTestScannerCompare]);

	useMemo(() => {
		if (mapTwo && scannerRouteCompare) {
			_addLayerKpiScannerRoute(
				mapTwo.control, scannerRouteCompare.driveTestPoints,
				scannerRouteCompare.driveTestLegend || '', formState.values.mapAutoZoom,
				'scannerRoute'
			);
		}
	}, [scannerRouteCompare]);

	useMemo(() => {
		if (mapOne && formState.values.customLayer) {
			_addCustomLayer(mapOne.control);
			_addCustomLayer(mapTwo.control);
		} else if (mapOne && !formState.values.customLayer) {
			mapOne.control.RemoveLayerById('CustomLayer');
			mapTwo.control.RemoveLayerById('CustomLayer');
		}
	}, [customLayer, formState.values.customLayer]);

	useEffect(() => {
		if (mapOne) {
			mapOne.instance.invalidateSize();
			mapTwo.instance.invalidateSize();
			mapOne.instance.sync(mapTwo.instance);
			mapTwo.instance.sync(mapOne.instance);
		}
	}, [formState.values.mapCompare, formState.values.mapFilters]);

	// if (mapOne) {
	// 	mapOne.instance.on('moveend', () => {
	// 		// callback
	// 		setTimeout(() => {
	// 			console.log('moveend');
	// 			const center = mapOne.instance.getCenter();
	// 			console.log(center);
	// 			mapTwo.instance.setView(center);
	// 		}, 1000);
	// 	});
	// }

	const _carriersColorsDialogClose = (event, reason) => {
		if (reason !== 'backdropClick') {
			dispatch(ToggleCarriersColorsDialog(false));
		}
	};

	const _carriersColorsPickerDialogClose = (event, reason) => {
		if (reason !== 'backdropClick') {
			dispatch(ToggleCarriersColorsPickerDialog(false));
			dispatch(ToggleCarriersColorsDialog(true));
		}
	};

	const _customLayerDialogClose = (event, reason) => {
		if (reason !== 'backdropClick') {
			dispatch(ToggleCustomLayerDialog(false));
		}
	};

	const _xcapToolDialogClose = (event, reason) => {
		if (reason !== 'backdropClick') {
			dispatch(ToggleXcapToolDialog(false));
		}
	};

	// console.log(formState.values.selectedKpiTech);
	// console.log(formState.values.selectedKpiScannerTech);

	let data;
	if (formState.values.selectedKpiTech !== undefined || formState.values.selectedKpiScannerTech !== undefined) {
		if (formState.values.selectedKpiTech !== undefined) {
			data = driveTest.chartData;
		} else if (formState.values.selectedKpiScannerTech !== undefined) {
			data = driveTestScanner.chartData;
		}
	}

	// console.log(formState.values.selectedKpiTechCompare);
	// console.log(formState.values.selectedKpiScannerTechCompare);

	let dataCompare;
	if (formState.values.selectedKpiTechCompare !== undefined || formState.values.selectedKpiScannerTechCompare !== undefined) {
		if (formState.values.selectedKpiTechCompare !== undefined) {
			dataCompare = driveTestCompare.chartData;
		} else if (formState.values.selectedKpiScannerTechCompare !== undefined) {
			dataCompare = driveTestScannerCompare.chartData;
		}
	}

	return (
		<Page className={classes.root} title="Map">
			{/* <Header module="Modules" view="Map" /> */}
			<div className={clsx(classes.container)}>
				<Card className={classes.container}>
					<CardContent>
						<Grid container spacing={1} style={{ marginBottom: '10px' }}>
							<Grid item md={12} xs={12}>
								<CompareBox />
							</Grid>
						</Grid>
						<Grid container spacing={1} style={{ marginBottom: '10px' }}>
							<Grid item md={formState.values.mapCompare ? 6 : 12}>
								<FiltersTop mapCompare={formState.values.mapCompare} />
							</Grid>
							<Grid item md={6} xs={6} hidden={!formState.values.mapCompare}>
								<FiltersTopCompare mapCompare={formState.values.mapCompare} />
							</Grid>
						</Grid>
						<Grid container spacing={1} style={{ marginBottom: '10px' }}>
							<Grid item md={2} xs={2} hidden={!formState.values.mapFilters}>
								<FiltersSide height="65vh" />
							</Grid>
							<Grid item md={formState.values.mapSize}>
								<Map id="dual-map" scale height="65vh" />
							</Grid>
							<Grid item md={formState.values.mapSize} hidden={!formState.values.mapCompare}>
								<Map id="dual-map-compare" scale height="65vh" />
							</Grid>
							<Grid item md={2} hidden={!formState.values.mapCompare || !formState.values.mapFilters}>
								<FiltersSideCompare height="65vh" />
							</Grid>
						</Grid>
						<Grid container spacing={1}>
							<Grid item md={12} xs={12}>
								<AdditionalInfo data={data} dataCompare={dataCompare} />
							</Grid>
						</Grid>
					</CardContent>
				</Card>
				{carriersColorsDialogVisibility && (
					<CarriersColorsDialog
						onClose={_carriersColorsDialogClose}
						open={carriersColorsDialogVisibility}
					/>
				)}
				{carriersColorsPickerDialogVisibility && (
					<CarriersColorsPickerDialog
						onClose={_carriersColorsPickerDialogClose}
						open={carriersColorsPickerDialogVisibility}
						currentColorCode={currentColorCode}
					/>
				)}
				{customLayerDialogVisibility && (
					<CustomLayerDialog
						onClose={_customLayerDialogClose}
						open={customLayerDialogVisibility}
					/>
				)}
				{xcapToolDialogVisibility && (
					<XcapToolFilesUploadDialog
						onClose={_xcapToolDialogClose}
						open={xcapToolDialogVisibility}
					/>
				)}
			</div>
		</Page>
	);
};

export default DualMap;
